import {GetWalletResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/service.types';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  Tabs,
  UnstyledButton,
  mediaTabletAndUp,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import {WalletType} from '../../utils/useActiveTab';
import {useNormalizeTabs} from '../../utils/useNormalizeTabs';
import {useWalletStateManager} from '../../utils/useWalletStateManager';

interface WalletTabsProps {
  activeTab: WalletType;
  onTabClick: (value: WalletType) => void;
  showBackButton: boolean;
}

export const WALLET_HEADER_HEIGHT = 60;

export const WalletTabs: React.FC<WalletTabsProps & GetWalletResponse> = ({
  tabs,
  activeTab,
  showBackButton,
  onTabClick,
}) => {
  const {cancel} = useWalletStateManager();
  const walletTabs = useNormalizeTabs(tabs);

  const [hasScrolled, setHasScrolled] = useState(false);
  const {ref} = useInViewEffect<HTMLDivElement>(inView =>
    setHasScrolled(!inView),
  );

  return (
    <>
      <div ref={ref} />
      <Wrapper data-white-background={hasScrolled} data-testid="walletHeader">
        <StyledTabs
          dataTestId="walletTabs"
          onTabClick={value => onTabClick(value as WalletType)}
          tabs={walletTabs.map(tab => ({...tab, label: tab.title}))}
          selectedTabValue={activeTab}
          activeTextColor={hasScrolled ? 'Black' : 'White'}
          inactiveTextColor="Fill.Grey.Inactive"
          $centerAlign={tabs.length > 1}
        />
        <CloseButton
          data-testid="walletCloseButton"
          onClick={cancel}
          aria-label={showBackButton ? 'Go back' : 'Close wallet'}
          $position={showBackButton ? 'left' : 'right'}
        >
          <CloseIcon
            asset={
              showBackButton ? GenericIconName.ArrowLeft : GenericIconName.Close
            }
            size="S"
            $color={hasScrolled ? COLORS.Black : COLORS.White}
          />
        </CloseButton>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  z-index: 3;

  transition-property: background-color, box-shadow;
  transition-duration: 150ms;
  transition-timing-function: ease-in;

  &[data-white-background='true'] {
    background-color: ${COLORS.White};
    box-shadow: 0px 2px 8px -3px ${COLORS.Black};
  }
`;

const CloseButton = styled(UnstyledButton)<{$position?: 'left' | 'right'}>`
  position: absolute;
  left: ${props => (props.$position === 'left' ? '16px' : 'unset')};
  right: ${props => (props.$position === 'right' ? '16px' : 'unset')};
  cursor: pointer;
  border: none;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled(Icon)<{$color: string}>`
  fill: ${props => props.$color};
`;

const StyledTabs = styled(Tabs)<{$centerAlign?: boolean}>`
  justify-content: center;

  [data-white-background='false'] & {
    background-color: transparent;
  }

  @media ${mediaTabletAndUp} {
    justify-content: ${props => (props.$centerAlign ? 'center' : 'left')};
    padding-left: ${props => (props.$centerAlign ? 0 : '20px')};
  }

  gap: 20px;

  > button {
    transition-property: color;
    transition-duration: 150ms;
    transition-timing-function: ease-in;

    flex: 0 1 auto;
    font-size: ${FONT_SIZES.px20};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
    border: none;
  }

  &&& {
    height: ${WALLET_HEADER_HEIGHT}px;
  }
`;
